import { SlideHeader } from "../../../SlideHeader";
import { SupLink } from "../../../SlideContext";

const BOTTOM_BADGES = [
  "Too manual, no automation.",
  "Optimized for defunct store algorithms.",
  "Provides only raw data, not insights + recommendations.",
];

type Corner = "tl" | "tr" | "bl" | "br";

const PRESSURES: Array<{ corner: Corner; lead: string; body: string }> = [
  {
    corner: "tl",
    lead: "KPIs decay",
    body: "Pause, and last month's gains are already slipping.",
  },
  {
    corner: "tr",
    lead: "Competitors iterate",
    body: "Rivals evolve their marketing constantly.",
  },
  {
    corner: "bl",
    lead: "Surfaces rerank",
    body: "Apple, Google & the AI assistants reshuffle results daily.",
  },
  {
    corner: "br",
    lead: "A global mandate",
    body: "More markets, more languages, more competitors.",
  },
];

export default function TheProblem() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:px-24 md:pt-16 md:pb-8 md:[&_header]:mb-8">
      <SlideHeader eyebrow="The Problem">
        <h1 className="text-3xl font-semibold leading-tight tracking-tight md:text-6xl md:leading-[1.1]">
          ASO has never been more valuable.
          <br className="hidden md:block" />{" "}
          … or harder to do correctly.
        </h1>
        <p className="mt-3 text-xl font-medium tracking-tight text-slate-700 md:mt-4 md:text-3xl">
          Your app is one of 4.5M+ on the App Store &amp; Google Play — and
          ~4,000 more launch every day<SupLink to="sources">1</SupLink>. You
          must iterate your GTM constantly, across ever more surfaces.
        </p>
      </SlideHeader>

      {/* Visual band — pressure diagram + decay curve */}
      <div className="grid grid-cols-1 gap-4 md:min-h-0 md:flex-1 md:grid-cols-[1.45fr_1fr] md:gap-6">
        <PressurePanel />
        <DecayPanel />
      </div>

      {/* Bottom Full-Width Panel — antiquated tooling (claim left, failures right) */}
      <div className="mt-4 flex flex-col gap-3 rounded-xl bg-slate-50 px-5 py-4 ring-1 ring-slate-200 md:mt-8 md:w-fit md:flex-row md:items-center md:gap-12 md:self-center md:px-10 md:py-5">
        <p className="text-balance text-base font-bold leading-snug tracking-tight text-slate-900 md:max-w-[380px] md:shrink-0 md:text-2xl">
          You're using antiquated tooling built for the pre-AI era of app
          marketing.
        </p>
        <ul className="flex flex-col gap-1.5 md:gap-2">
          {BOTTOM_BADGES.map((b) => (
            <li
              key={b}
              className="flex items-start gap-2.5 text-sm leading-snug text-slate-700 md:text-base"
            >
              <span aria-hidden="true" className="shrink-0 font-bold text-rose-500">
                ✕
              </span>
              {b}
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-3 text-center md:mt-4">
        <p className="text-balance text-lg font-bold italic leading-snug text-violet-700 md:text-3xl">
          ASO became continuous. Your tools aren't ready.
        </p>
      </div>
    </div>
  );
}

/* ---------- Pressure diagram ---------- */

function PressurePanel() {
  return (
    <div className="flex flex-col rounded-xl bg-white p-5 pb-3 shadow-sm ring-1 ring-slate-200 md:p-6 md:pb-3">
      <h3 className="text-lg font-bold leading-tight tracking-tight text-blue-600 md:text-2xl">
        Four forces. Never idle.
      </h3>
      <p className="text-sm italic leading-snug text-slate-600 md:text-base">
        The environment moves whether you do or not.
      </p>

      <div className="mt-1 grid min-h-0 max-h-[220px] flex-1 grid-cols-[1fr_auto_1fr] items-center gap-x-3 md:mt-2">
        <div className="flex h-full min-h-0 flex-col justify-around gap-3 py-1">
          <PressureLabel {...PRESSURES[0]} />
          <PressureLabel {...PRESSURES[2]} />
        </div>
        <AppTile />
        <div className="flex h-full min-h-0 flex-col justify-around gap-3 py-1">
          <PressureLabel {...PRESSURES[1]} />
          <PressureLabel {...PRESSURES[3]} />
        </div>
      </div>
    </div>
  );
}

function PressureLabel({
  corner,
  lead,
  body,
}: {
  corner: Corner;
  lead: string;
  body: string;
}) {
  const isLeft = corner === "tl" || corner === "bl";
  return (
    <div className={isLeft ? "text-right" : "text-left"}>
      <p className="text-sm font-bold leading-tight text-slate-900 md:text-base">
        {lead}
      </p>
      <p className="mt-0.5 text-xs leading-snug text-slate-600 md:text-sm">
        {body}
      </p>
    </div>
  );
}

// Base arrow points ↘ with its tip at the glyph's bottom-right; each corner
// placement rotates it so the tip lands nearest the tile corner, aimed at it.
function PressureArrow({
  rotation,
  className,
}: {
  rotation: number;
  className?: string;
}) {
  return (
    <svg
      viewBox="0 0 36 36"
      aria-hidden="true"
      className={`h-7 w-7 shrink-0 md:h-9 md:w-9 ${className ?? ""}`}
      style={{ transform: `rotate(${rotation}deg)` }}
    >
      <line
        x1="5"
        y1="5"
        x2="21"
        y2="21"
        stroke="#dc2626"
        strokeWidth="2.5"
        strokeDasharray="5,4"
        strokeLinecap="round"
      />
      <path d="M 30 30 L 27.1 20.1 L 20.1 27.1 z" fill="#dc2626" />
    </svg>
  );
}

function AppTile() {
  return (
    <div className="flex flex-col items-center px-8 py-1 md:px-10">
      <div className="relative">
        {/* Arrows pinned to the tile's corners, each aimed at its corner */}
        <PressureArrow rotation={0} className="absolute -left-8 -top-8 md:-left-10 md:-top-10" />
        <PressureArrow rotation={90} className="absolute -right-8 -top-8 md:-right-10 md:-top-10" />
        <PressureArrow rotation={270} className="absolute -left-8 -bottom-8 md:-left-10 md:-bottom-10" />
        <PressureArrow rotation={180} className="absolute -right-8 -bottom-8 md:-right-10 md:-bottom-10" />
        <div className="flex h-16 w-16 items-center justify-center rounded-[22%] bg-blue-600 shadow-md md:h-20 md:w-20">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.6}
          aria-hidden="true"
          className="h-8 w-8 text-white md:h-11 md:w-11"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M3.75 6A2.25 2.25 0 0 1 6 3.75h2.25A2.25 2.25 0 0 1 10.5 6v2.25a2.25 2.25 0 0 1-2.25 2.25H6a2.25 2.25 0 0 1-2.25-2.25V6ZM3.75 15.75A2.25 2.25 0 0 1 6 13.5h2.25a2.25 2.25 0 0 1 2.25 2.25V18a2.25 2.25 0 0 1-2.25 2.25H6A2.25 2.25 0 0 1 3.75 18v-2.25ZM13.5 6a2.25 2.25 0 0 1 2.25-2.25H18A2.25 2.25 0 0 1 20.25 6v2.25A2.25 2.25 0 0 1 18 10.5h-2.25a2.25 2.25 0 0 1-2.25-2.25V6ZM13.5 15.75a2.25 2.25 0 0 1 2.25-2.25H18a2.25 2.25 0 0 1 2.25 2.25V18A2.25 2.25 0 0 1 18 20.25h-2.25A2.25 2.25 0 0 1 13.5 18v-2.25Z"
          />
        </svg>
        </div>
      </div>
      <p className="mt-2 text-sm font-semibold text-slate-900 md:text-base">
        Your app
      </p>
      <p className="text-xs text-slate-500 md:text-sm">one of 4.5M+</p>
    </div>
  );
}

/* ---------- Decay curve ---------- */

function DecayPanel() {
  return (
    <div className="flex flex-col rounded-xl bg-white p-5 pb-3 shadow-sm ring-1 ring-slate-200 md:p-6 md:pb-3">
      <h3 className="text-lg font-bold leading-tight tracking-tight text-blue-600 md:text-2xl">
        The compounding gap
      </h3>
      <p className="text-sm italic leading-snug text-slate-600 md:text-base">
        Organic visibility &amp; conversion decide your CAC, ROAS &amp;
        install growth.
      </p>

      <div className="flex min-h-0 flex-1 items-center justify-center py-2">
        <svg viewBox="0 0 360 230" className="h-48 w-full md:h-full md:max-h-64" role="img" aria-label="Conceptual chart: with continuous ASO an app's visibility and conversion keep rising; pause and they decay — the gap compounds.">
          {/* Axes */}
          <path
            d="M 28 18 L 28 198 L 348 198"
            fill="none"
            stroke="#cbd5e1"
            strokeWidth="1.5"
          />
          <path
            d="M 342 193 L 348 198 L 342 203"
            fill="none"
            stroke="#cbd5e1"
            strokeWidth="1.5"
          />
          <text x="336" y="216" textAnchor="end" fontSize="11" fill="#64748b">
            Time
          </text>

          {/* Shared path up to the fork */}
          <path
            d="M 28 126 C 50 123 72 120 92 118"
            fill="none"
            stroke="#475569"
            strokeWidth="2.5"
            strokeLinecap="round"
          />
          <circle cx="92" cy="118" r="3.5" fill="#475569" />

          {/* Continuous ASO — compounds upward */}
          <path
            d="M 92 118 C 150 112 230 92 290 64 C 315 52 332 42 344 34"
            fill="none"
            stroke="#2563eb"
            strokeWidth="2.5"
            strokeLinecap="round"
          />
          <text
            x="344"
            y="24"
            textAnchor="end"
            fontSize="12"
            fontWeight="600"
            fill="#2563eb"
          >
            Continuous ASO
          </text>

          {/* You pause — decays */}
          <path
            d="M 92 118 C 140 128 200 152 250 170 C 290 183 320 189 344 191"
            fill="none"
            stroke="#dc2626"
            strokeWidth="2.5"
            strokeLinecap="round"
          />
          <text
            x="344"
            y="180"
            textAnchor="end"
            fontSize="12"
            fontWeight="600"
            fill="#dc2626"
          >
            You pause
          </text>
        </svg>
      </div>
    </div>
  );
}
